import React from "react";
import { TpmBar, TpmDonut } from "../chartKit";
import { HOME_COLORS, HOME_SERIES, HOME_TRACK } from "../homeTheme";

// Shared building blocks for the home report modals: KPI tiles, chart cards and
// the chart.js option factories.
//
// Design rules for these charts, all of them deliberate:
//   * every value is printed on the chart (see `valueLabels` below), so nothing
//     is only reachable by hovering — and the PDF/Word export carries the
//     numbers too;
//   * one muted palette (see homeTheme) instead of a seven-colour rainbow;
//   * no chart junk — no gridlines, no axis borders, no value axis at all
//     (the bars carry their numbers), no legend where the axis already labels;
//   * animation off, so the html-to-image capture grabs a painted canvas.

// Palette. Names kept from the previous version so the report bodies keep
// reading naturally; the values now come from the home palette.
export const BRAND = HOME_COLORS.brand;
export const BRAND_DARK = "#6A113C";
export const SERIES = HOME_SERIES;
export const FLAG_COLORS = {
  notAddressed: HOME_COLORS.alert,
  inProgress: HOME_COLORS.warn,
  onHold: HOME_COLORS.stalled,
  resolved: HOME_COLORS.ok,
  archived: HOME_COLORS.neutral,
};
export const OK_COLOR = HOME_COLORS.ok;
export const LATE_COLOR = HOME_COLORS.alert;
export const REST_COLOR = HOME_TRACK;

// Coerce anything the API hands back into a safe number.
export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Percentage helper with divide-by-zero guard.
export const pct = (part, whole) =>
  whole > 0 ? Math.round((num(part) / num(whole)) * 100) : 0;

export const KpiTile = ({ label, value, suffix, color }) => (
  <div className="reportKpi">
    <span className="reportKpiValue">
      {value}
      {suffix ? <span className="reportKpiSuffix">{suffix}</span> : null}
    </span>
    <span className="reportKpiLabel">
      {color ? (
        <span className="reportKpiDot" style={{ background: color }} />
      ) : null}
      {label}
    </span>
  </div>
);

export const KpiRow = ({ children }) => (
  <div className="reportKpiRow">{children}</div>
);

export const ChartCard = ({ title, hint, children }) => (
  <div className="reportChartCard">
    <div className="reportChartHead">
      <span className="reportChartTitle">{title}</span>
      {hint ? <span className="reportChartHint">{hint}</span> : null}
    </div>
    <div className="reportChartBody">{children}</div>
  </div>
);

export const ReportGrid = ({ children }) => (
  <div className="reportChartGrid">{children}</div>
);

export const EmptyNote = ({ text = "No data available" }) => (
  <div className="reportEmpty">{text}</div>
);

// ---- Charts ----
// The plugin, the option normaliser and the components live in ../chartKit,
// shared with the TPM / IP / Visit Data summary tabs. What is left here is the
// reports' own choices on top of that: no value axis at all (the bars carry
// their numbers), no legend on bars, and a thin donut ring.
export { valueLabels } from "../chartKit";

export const donutOptions = {
  // Thin ring: the numbers are the content, the ring is just proportion.
  cutout: "74%",
  // The chart cards give the canvas a fixed height.
  maintainAspectRatio: false,
};

export const barOptions = (horizontal = false) => ({
  indexAxis: horizontal ? "y" : "x",
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      beginAtZero: true,
      // Category labels on the axis that holds them; the value axis is dropped
      // altogether, so its gridlines would measure nothing.
      grid: { display: false },
      ticks: { display: !horizontal, autoSkip: false },
    },
    y: {
      beginAtZero: true,
      grid: { display: false },
      ticks: { display: horizontal, autoSkip: false },
      // chart.js measures label widths before the Poppins webfont applies, so a
      // long category ("Completed") ends up clipped. Buy back the difference.
      afterFit: (scale) => {
        if (horizontal) scale.width += 16;
      },
    },
  },
});

export const ReportBar = TpmBar;
export const ReportDonut = TpmDonut;

// Build a single-series bar dataset from [{label, value}] pairs.
export const barData = (rows, colors) => ({
  labels: rows.map((r) => r.label),
  datasets: [
    {
      label: "Count",
      data: rows.map((r) => num(r.value)),
      backgroundColor: rows.map(
        (r, i) => r.color || (colors.length ? colors[i % colors.length] : BRAND)
      ),
      borderWidth: 0,
      borderRadius: 4,
      // Slim bars read as data rather than as blocks of colour.
      maxBarThickness: 22,
    },
  ],
});

// Build a donut dataset from [{label, value, color}] pairs.
export const donutData = (rows) => ({
  labels: rows.map((r) => r.label),
  datasets: [
    {
      data: rows.map((r) => num(r.value)),
      backgroundColor: rows.map((r) => r.color),
      hoverBackgroundColor: rows.map((r) => r.color),
      borderWidth: 0,
      spacing: 2,
    },
  ],
});

// True when every value in a set of rows is zero/empty — used to swap in the
// EmptyNote instead of rendering a blank chart.
export const allZero = (rows) => rows.every((r) => num(r.value) === 0);
